// DataExportsCard
// =====================================================================
// Admin-only download tile for the THCO private export bundles.
// Files live under /app/private_exports/ on the backend (off any web-served
// path) and are streamed by /api/admin/private-export-download. The backend
// restricts that endpoint to a Joshua-only allowlist (Neil is intentionally
// excluded — the X-Ray cohort is fine for him, but raw CSV bundles carry
// full free-text + telemetry under the DPA and stay with THCO leadership).
//
// We mirror that allowlist on the UI so Neil (or any other non-Joshua
// admin) does not even see the card. Backend remains the source of truth.

import { useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, FileArchive, FileText, Hash, ImageIcon } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Joshua-only. Keep in sync with `_DATA_EXPORT_ALLOWLIST` in /app/backend/server.py.
const DATA_EXPORT_EMAIL_ALLOWLIST = new Set(["joshua@thcohq.com"]);

function getCurrentEmail() {
  try {
    const info = JSON.parse(localStorage.getItem("userInfo") || "null");
    if (info?.email) return String(info.email).trim().toLowerCase();
  } catch (_) {}
  try {
    const info = JSON.parse(localStorage.getItem("adminUser") || "null");
    if (info?.email) return String(info.email).trim().toLowerCase();
  } catch (_) {}
  return "";
}

const FOLDER = {
  master:  "sagicor_tca_export_2026-05-25",
  sbbl:    "sagicor_tca_export_sbbl_2026-05-26",
  d11:     "sagicor_tca_export_d11_ai_2026-05-26",
  full_ai: "sagicor_tca_export_full_ai_2026-05-27",
};

const EXPORTS = [
  {
    id: "master",
    label: "Master Export",
    sublabel: "Full read-only data extract · 2026-05-25",
    meta: "49.5 MB · 23 files · 2,322 candidates",
    file: `${FOLDER.master}/sagicor_tca_export_2026-05-25.zip`,
    companions: [
      { id: "dict", icon: FileText, label: "Data Dictionary",
        file: `${FOLDER.master}/DATA_DICTIONARY.md` },
      { id: "comp", icon: FileText, label: "Completeness Report",
        file: `${FOLDER.master}/COMPLETENESS_REPORT.md` },
      { id: "rows", icon: ImageIcon, label: "Row Counts (PNG)",
        file: `${FOLDER.master}/row_counts_summary.png` },
      { id: "sha",  icon: Hash, label: "SHA-256",
        file: `${FOLDER.master}/sagicor_tca_export_2026-05-25.zip.sha256` },
    ],
  },
  {
    id: "sbbl",
    label: "SBBL Sub-cohort",
    sublabel: "Sagicor Bank Barbados (10 candidates) · 2026-05-26",
    meta: "293 KB",
    file: `${FOLDER.sbbl}/sagicor_tca_export_sbbl_2026-05-26.zip`,
    companions: [
      { id: "dict", icon: FileText, label: "Data Dictionary",
        file: `${FOLDER.sbbl}/DATA_DICTIONARY.md` },
      { id: "comp", icon: FileText, label: "Completeness Report",
        file: `${FOLDER.sbbl}/COMPLETENESS_REPORT.md` },
      { id: "sha",  icon: Hash, label: "SHA-256",
        file: `${FOLDER.sbbl}/sagicor_tca_export_sbbl_2026-05-26.zip.sha256` },
    ],
  },
  {
    id: "d11",
    label: "D11 · AI Enablement",
    sublabel: "AI-posture + adoption signal + cross-tabs · 2026-05-26",
    meta: "9.3 MB · 10 CSVs + reports",
    file: `${FOLDER.d11}/sagicor_tca_export_d11_ai_2026-05-26.zip`,
    companions: [
      { id: "dict", icon: FileText, label: "Data Dictionary",
        file: `${FOLDER.d11}/DATA_DICTIONARY.md` },
      { id: "rows", icon: FileText, label: "Row Counts",
        file: `${FOLDER.d11}/ROW_COUNTS.md` },
      { id: "sha",  icon: Hash, label: "SHA-256",
        file: `${FOLDER.d11}/sagicor_tca_export_d11_ai_2026-05-26.zip.sha256` },
    ],
  },
  {
    id: "full_ai",
    label: "Full Raw + AI Findings",
    sublabel: "Re-export + flattened findings + effort & coding · 2026-05-27",
    meta: "56.6 MB · 35 CSVs · finding → AI capability chain",
    file: `${FOLDER.full_ai}/sagicor_tca_export_full_ai_2026-05-27.zip`,
    companions: [
      { id: "dict", icon: FileText, label: "Data Dictionary",
        file: `${FOLDER.full_ai}/DATA_DICTIONARY.md` },
      { id: "rows", icon: FileText, label: "Row Counts",
        file: `${FOLDER.full_ai}/ROW_COUNTS.md` },
      { id: "sha",  icon: Hash, label: "SHA-256",
        file: `${FOLDER.full_ai}/sagicor_tca_export_full_ai_2026-05-27.zip.sha256` },
    ],
  },
];

async function downloadFile(filePath, suggestedName, setBusy) {
  setBusy(true);
  try {
    const tok =
      localStorage.getItem("adminToken") ||
      localStorage.getItem("executiveToken") ||
      localStorage.getItem("authToken");
    if (!tok) {
      toast.error("Not signed in. Please log in as Joshua to download.");
      return;
    }
    const res = await fetch(
      `${API}/api/admin/private-export-download?file=${encodeURIComponent(filePath)}`,
      { headers: { Authorization: `Bearer ${tok}` } }
    );
    if (!res.ok) {
      const body = await res.text();
      toast.error(`Download failed (HTTP ${res.status}): ${body.slice(0, 120)}`);
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = suggestedName;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`${suggestedName} downloaded`);
  } catch (e) {
    toast.error(`Download failed: ${e?.message || e}`);
  } finally {
    setBusy(false);
  }
}

export default function DataExportsCard() {
  const [busy, setBusy] = useState({});
  const setOne = (k, v) => setBusy((b) => ({ ...b, [k]: v }));

  // Hide entirely if the current user is not on the data-export allowlist.
  // Backend enforces the same allowlist; this is a UX shortcut so the card
  // simply doesn't render for Neil or any other non-Joshua admin.
  const currentEmail = getCurrentEmail();
  if (!DATA_EXPORT_EMAIL_ALLOWLIST.has(currentEmail)) {
    return null;
  }

  return (
    <div
      data-testid="data-exports-card"
      className="mb-6"
      style={{
        background: "var(--surface-card)",
        border: "var(--border-width) solid var(--border-default)",
        borderRadius: "1rem",
        boxShadow: "var(--shadow-card)",
        overflow: "hidden",
      }}
    >
      <div style={{ height: 4, background: "linear-gradient(90deg, #6b7280 0%, #0a3147 100%)" }} />
      <div style={{ padding: "20px 24px" }}>
        <div className="flex items-center gap-3 mb-4">
          <FileArchive className="w-6 h-6" style={{ color: "#0a3147" }} />
          <div>
            <div className="text-[10px] font-semibold tracking-widest uppercase" style={{ color: "#0099CC" }}>
              THCO Private Exports · DPA-controlled
            </div>
            <div className="text-2xl font-semibold tracking-tight" style={{ color: "var(--text-primary)" }}>
              Data Exports
            </div>
            <div className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
              Streamed directly from the backend (Content-Disposition: attachment).
              Forward via secure channel only.
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {EXPORTS.map((x) => (
            <div
              key={x.id}
              data-testid={`export-tile-${x.id}`}
              className="flex flex-col justify-between"
              style={{
                border: "1px solid #cfd9e0",
                borderRadius: 10,
                padding: "14px 16px",
                background: "#fbfdff",
              }}
            >
              <div>
                <div className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
                  {x.label}
                </div>
                <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
                  {x.sublabel}
                </div>
                <div className="text-[11px] mt-2" style={{ color: "#5a6b78", fontFamily: "ui-monospace, monospace" }}>
                  {x.meta}
                </div>
              </div>

              <button
                data-testid={`download-btn-${x.id}`}
                onClick={() =>
                  downloadFile(x.file, x.file.split("/").pop(), (v) => setOne(x.id, v))
                }
                disabled={!!busy[x.id]}
                className="mt-3 inline-flex items-center justify-center gap-2 text-xs font-semibold tracking-wide"
                style={{
                  padding: "9px 14px",
                  background: "linear-gradient(135deg, #00B5EF 0%, #82C341 100%)",
                  color: "#FFFFFF",
                  borderRadius: 8,
                  boxShadow: "0 1px 3px rgba(0,153,204,0.30)",
                  border: 0,
                  cursor: busy[x.id] ? "wait" : "pointer",
                  opacity: busy[x.id] ? 0.7 : 1,
                  width: "100%",
                }}
              >
                {busy[x.id] ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Downloading…
                  </>
                ) : (
                  <>
                    <Download className="w-3.5 h-3.5" />
                    Download ZIP
                  </>
                )}
              </button>

              {x.companions?.length > 0 && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-wider mb-1.5"
                       style={{ color: "#5a6b78", fontWeight: 600, letterSpacing: "0.08em" }}>
                    Companions
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {x.companions.map((c) => {
                      const key = `${x.id}_${c.id}`;
                      const Icon = c.icon;
                      return (
                        <button
                          key={c.id}
                          data-testid={`download-companion-${x.id}-${c.id}`}
                          onClick={() =>
                            downloadFile(c.file, c.file.split("/").pop(), (v) => setOne(key, v))
                          }
                          disabled={!!busy[key]}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-wide"
                          style={{
                            padding: "5px 9px",
                            background: "#fff",
                            color: "#0a3147",
                            borderRadius: 6,
                            border: "1px solid #cfd9e0",
                            cursor: busy[key] ? "wait" : "pointer",
                            opacity: busy[key] ? 0.6 : 1,
                          }}
                          title={c.file}
                        >
                          {busy[key] ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Icon className="w-3 h-3" style={{ color: "#5a6b78" }} />
                          )}
                          {c.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
